import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';

const FAQSection: React.FC = () => {
  const { t, language } = useLanguage();
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const faqs = language === 'pt' ? [
    {
      question: 'O que é "automação com IA" para uma pequena empresa?',
      answer: 'É usar inteligência artificial para tratar das tarefas repetitivas do dia a dia — responder a clientes, qualificar leads, enviar propostas, criar conteúdo, gerar relatórios. Em vez de contratar mais uma pessoa, configuramos um sistema que faz tudo automaticamente, 24 horas por dia. Começa em pequeno e expande à medida que vê resultados.'
    },
    {
      question: 'Quanto custa criar um website para a minha pequena empresa?',
      answer: 'Os nossos pacotes começam em apenas 150€ para um website profissional, com domínio e SSL incluídos. Para projetos com loja online ou integrações IA o orçamento é feito à medida. Preços transparentes, sem letras pequenas — e planos de pagamento flexíveis quando precisa.'
    },
    {
      question: 'Quanto custa uma automação IA?',
      answer: 'Depende do que quer automatizar. Um chatbot simples para o website começa em cerca de 300€ de setup + uma mensalidade baixa. Workflows mais avançados (CRM, faturação, WhatsApp, agenda) são orçamentados após a chamada gratuita, onde percebemos exatamente o que está a perder tempo a fazer. Foco no retorno, não em horas faturadas.'
    },
    {
      question: 'Quanto tempo demora até estar online?',
      answer: 'Entregamos websites profissionais em apenas 5 dias úteis. Automações simples ficam prontas em 1-2 semanas; projetos com várias integrações em 3-4 semanas. Trabalhamos por sprints curtos e não desaparecemos a meio do projeto.'
    },
    {
      question: 'Preciso de saber alguma coisa de tecnologia para usar?',
      answer: 'Não. Desenhamos tudo para que a sua equipa consiga usar sem stress — formação incluída, documentação em português e suporte continuado. A ideia é exatamente o contrário: você foca-se no negócio, nós tratamos da parte técnica.'
    },
    {
      question: 'Continuam a apoiar depois da entrega?',
      answer: 'Sim. Oferecemos planos de manutenção mensal acessíveis que incluem atualizações, backups, monitorização e suporte. Nunca o deixamos sozinho com o software a meio do caminho. Pode cancelar quando quiser, sem fidelizações longas.'
    },
    {
      question: 'O que vos torna diferentes das grandes agências?',
      answer: 'Trabalhamos exclusivamente com pequenas empresas. Conhecemos os orçamentos apertados, o tempo curto e a necessidade de resultados rápidos. Sem propostas de 20 páginas, sem reuniões intermináveis, sem termos técnicos. Preço fixo combinado à partida, entrega rápida, e tratamos o seu negócio como se fosse o nosso.'
    }
  ] : [
    {
      question: 'What does "AI automation" actually mean for a small business?',
      answer: 'It means using AI to take care of the repetitive daily work — replying to customers, qualifying leads, sending proposals, creating content, generating reports. Instead of hiring another person, we set up a system that does it all automatically, 24/7. Start small, expand as you see results.'
    },
    {
      question: 'How much does a professional website cost?',
      answer: 'Our packages start at just €150 for a professional website, with domain and SSL included. E-commerce and AI-integrated sites are quoted individually. Transparent pricing, no fine print — and flexible payment plans when you need them.'
    },
    {
      question: 'How much does AI automation cost?',
      answer: 'It depends on what you want to automate. A simple website chatbot starts at around €300 setup plus a low monthly fee. More advanced workflows (CRM, invoicing, WhatsApp, scheduling) are quoted after the free call, where we map out exactly what is eating your time. Focused on return, not billable hours.'
    },
    {
      question: 'How long does it take to go live?',
      answer: 'Professional websites in just 5 working days. Simple automations in 1-2 weeks; multi-integration projects in 3-4. We work in short sprints and we don\'t disappear mid-project.'
    },
    {
      question: 'Do I need to be tech-savvy to use it?',
      answer: 'No. Everything is designed so your team can use it without stress — training included, documentation in plain language, ongoing support. The whole idea is the opposite: you focus on the business, we handle the tech.'
    },
    {
      question: 'Do you keep supporting us after delivery?',
      answer: 'Yes. We offer affordable monthly maintenance plans covering updates, backups, monitoring and support. We never leave you alone halfway. Cancel anytime, no long lock-ins.'
    },
    {
      question: 'What makes you different from big agencies?',
      answer: 'We only work with small businesses. We get tight budgets, short time, and the need for fast results. No 20-page proposals, no endless meetings, no jargon. Fixed price agreed upfront, fast delivery, and we treat your business like our own.'
    }
  ];

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };

  return (
    <>
      <SafeJsonLd data={faqStructuredData} />
      <section
        id="faq"
        ref={ref}
        className="relative py-24 md:py-32 bg-secondary/40 border-y border-border/60"
        aria-labelledby="faq-heading"
      >
        <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-12 gap-6 mb-16 md:mb-20"
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.8 }}
          >
            <div className="col-span-12 md:col-span-7">
              <span className="eyebrow">— {t('faq.eyebrow')}</span>
              <h2
                id="faq-heading"
                className="mt-4 font-display text-foreground text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.04] tracking-tight"
              >
                {t('faq.title')}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                {t('faq.subtitle')}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <Accordion
              type="single"
              collapsible
              className="w-full border border-border/60 rounded-2xl overflow-hidden bg-card/60 backdrop-blur-sm"
            >
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-b border-border/60 last:border-0 px-4 md:px-8 data-[state=open]:bg-card transition-colors"
                >
                  <AccordionTrigger className="text-left text-base md:text-lg font-medium text-foreground hover:no-underline hover:text-lime smooth-transition py-6 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]]:text-lime">
                    <span className="flex items-center gap-5">
                      <span className="font-mono-tag text-xs uppercase tracking-[0.32em] text-muted-foreground/70 hidden sm:inline">
                        / 0{index + 1}
                      </span>
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 leading-relaxed text-sm md:text-base md:pl-[68px]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
